import { useState } from 'react';
import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';

import { IntegrationMemberRemoveDocument } from 'src/graphql/actions/integration/integrationMemberRemove.mutation';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ClientError, getApiError } from 'src/utils/errors';
import { Button } from '@/components/ui/button';

import { refetchIntegrationMembers } from '../api';

type Props = {
  id: string;
  children: React.ReactNode;
};

const RemoveMemberDialog = ({ id, children }: Props) => {
  const [open, setOpen] = useState(false);
  const [removeMember, { loading }] = useMutation(IntegrationMemberRemoveDocument, {
    onCompleted: refetchIntegrationMembers,
  });

  const handleConfirm = async () => {
    try {
      const { data } = await removeMember({ variables: { id } });
      if (data?.integrationMemberRemove?.success) {
        setOpen(false);
        toast.success('Access removed');
      } else {
        throw new ClientError(data?.integrationMemberRemove?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure you want to remove access?</AlertDialogTitle>
          <AlertDialogDescription>
            This team member will no longer have access to this integration and won&apos;t be able to use it to add
            assets to their products.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button onClick={handleConfirm} variant="destructive" isLoading={loading} disabled={loading}>
            Remove access
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default RemoveMemberDialog;
